import React from "react";

// Icons pulled directly from pulse-boltos.vercel.app's own rendered markup
// rather than approximated with generic MUI/lucide equivalents - kept in
// their own file so both PulsePage.tsx and PulseDesignerViews.tsx can use
// them without a circular import between those two files.

export const IrisIcon: React.FC<{ fontSize?: "small" | "medium" }> = ({ fontSize = "small" }) => (
  <svg
    viewBox="0 0 250 250"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    width={fontSize === "small" ? 20 : 24}
    height={fontSize === "small" ? 20 : 24}
    aria-label="IRIS"
    role="img"
  >
    <path
      d="M125.002 62.5C59.877 62.5 9.76758 119.875 9.76758 119.875L5.12695 125L9.76758 130.125C9.76758 130.125 55.4551 182.219 116.213 187.016C119.111 187.375 122.01 187.5 125.002 187.5C127.994 187.5 130.893 187.375 133.791 187.008C194.549 182.219 240.236 130.133 240.236 130.133L244.877 125L240.236 119.875C240.236 119.875 190.127 62.5 125.002 62.5ZM125.002 78.125C142.213 78.125 158.08 82.8281 171.877 89.1094C177.01 97.5739 179.713 107.288 179.689 117.188C179.723 130.655 174.782 143.66 165.815 153.708C156.849 163.756 144.487 170.139 131.104 171.633C130.947 171.664 130.768 171.602 130.619 171.633C128.752 171.719 126.893 171.875 125.002 171.875C122.924 171.875 120.916 171.75 118.9 171.633C105.516 170.139 93.1554 163.756 84.1887 153.708C75.2221 143.66 70.2814 130.655 70.3145 117.188C70.3145 106.992 73.0645 97.5 77.8848 89.3516H77.6426C91.541 82.9453 107.604 78.125 125.002 78.125ZM56.6426 101.078C55.372 106.355 54.7166 111.76 54.6895 117.188C54.6895 130.891 58.5957 143.711 65.4316 154.539C51.5631 146.414 38.8369 136.481 27.5879 125C36.4456 116.069 46.1795 108.06 56.6426 101.078ZM193.361 101.078C203.824 108.057 213.558 116.072 222.416 125C211.167 136.481 198.441 146.414 184.572 154.539C191.619 143.357 195.344 130.404 195.314 117.188C195.314 111.602 194.58 106.273 193.361 101.078Z"
      fill="currentColor"
    />
    <path
      d="M116.658 105.414L90 113.69L116.486 121.621L124.914 148L132.826 121.621L160 113.69L132.654 105.414L124.914 78L116.658 105.414Z"
      fill="currentColor"
    />
  </svg>
);

// Single 4-point spark - just the small accent mark cropped out of IrisIcon above (same path,
// same source), not a generic MUI "AutoAwesome" glyph. The reference's hover-jump icon is this
// ONE diamond/star, not AutoAwesome's main-star-plus-secondary-sparkle pair - using this instead
// of AutoAwesomeRoundedIcon is what actually matches it.
export const IrisSparkIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg viewBox="85 73 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width={size} height={size} aria-hidden="true">
    <path
      d="M116.658 105.414L90 113.69L116.486 121.621L124.914 148L132.826 121.621L160 113.69L132.654 105.414L124.914 78L116.658 105.414Z"
      fill="currentColor"
    />
  </svg>
);

// Official IRIS wordmark (light variant: #101014 letters, gradient eye+sparkle mark) -
// supplied directly by the human as the source-of-truth logo file, not approximated.
// Source: hive/docs/assets/iris-logo-light.svg (viewBox 0 0 630 294, ~2.14:1).
export const IrisWordmark: React.FC<{ height?: number; className?: string }> = ({ height = 34, className }) => (
  <svg
    width={height * (630 / 294)}
    height={height}
    viewBox="0 0 630 294"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-label="IRIS"
    role="img"
    className={className}
  >
    <path d="M536.923 200.8C532.123 200.8 527.323 200.15 522.523 198.85C517.723 197.45 513.273 195.3 509.173 192.4C505.173 189.4 501.823 185.55 499.123 180.85C496.423 176.05 494.773 170.3 494.173 163.6H513.373C514.273 168.7 515.973 172.65 518.473 175.45C521.073 178.15 524.073 180.05 527.473 181.15C530.873 182.25 534.173 182.8 537.373 182.8C541.073 182.8 544.323 182.2 547.123 181C550.023 179.8 552.273 178.15 553.873 176.05C555.573 173.95 556.423 171.6 556.423 169C556.423 166.1 555.623 163.5 554.023 161.2C552.423 158.9 548.873 157.25 543.373 156.25L526.423 153.1C517.523 151.4 510.623 147.9 505.723 142.6C500.823 137.2 498.373 130.85 498.373 123.55C498.373 116.95 500.023 111.35 503.323 106.75C506.623 102.05 511.073 98.45 516.673 95.95C522.273 93.45 528.473 92.2 535.273 92.2C542.373 92.2 548.723 93.4 554.323 95.8C560.023 98.2 564.623 101.95 568.123 107.05C571.723 112.05 573.873 118.55 574.573 126.55H555.073C554.373 122.05 553.023 118.65 551.023 116.35C549.023 113.95 546.673 112.3 543.973 111.4C541.273 110.5 538.573 110.05 535.873 110.05C533.373 110.05 530.673 110.45 527.773 111.25C524.973 112.05 522.573 113.4 520.573 115.3C518.673 117.2 517.723 119.85 517.723 123.25C517.723 126.05 518.723 128.55 520.723 130.75C522.823 132.95 526.373 134.5 531.373 135.4L546.973 138.25C555.873 139.85 562.923 143 568.123 147.7C573.323 152.3 575.923 159.2 575.923 168.4C575.923 173.6 574.773 178.2 572.473 182.2C570.273 186.2 567.273 189.6 563.473 192.4C559.773 195.1 555.623 197.2 551.023 198.7C546.423 200.1 541.723 200.8 536.923 200.8Z" fill="#101014" />
    <path d="M462.467 199L462.467 137.5H483.467L483.467 199H462.467Z" fill="#101014" />
    <path d="M468.472 108.882L454 113.374L468.378 117.68L472.953 132L477.248 117.68L492 113.374L477.155 108.882L472.953 94L468.472 108.882Z" fill="#101014" />
    <path d="M366.52 199V94H411.07C419.57 94 426.57 95.45 432.07 98.35C437.67 101.15 441.82 105.05 444.52 110.05C447.32 114.95 448.72 120.5 448.72 126.7C448.72 131.5 447.67 135.95 445.57 140.05C443.47 144.05 440.67 147.5 437.17 150.4C440.97 153 443.87 156.35 445.87 160.45C447.97 164.55 449.12 169.25 449.32 174.55L450.52 199H429.97L428.32 174.55C428.12 171.05 427.42 168.2 426.22 166C425.12 163.7 423.37 162 420.97 160.9C418.57 159.8 415.32 159.25 411.22 159.25H387.52V199H366.52ZM387.52 142.3H409.72C415.92 142.3 420.42 140.9 423.22 138.1C426.02 135.3 427.42 131.5 427.42 126.7C427.42 121.8 426.02 118 423.22 115.3C420.42 112.5 415.92 111.1 409.72 111.1H387.52V142.3Z" fill="#101014" />
    <path d="M331.363 199V94H352.363V199H331.363Z" fill="#101014" />
    <path d="M175.002 84.5C109.877 84.5 59.7676 141.875 59.7676 141.875L55.127 147L59.7676 152.125C59.7676 152.125 105.455 204.219 166.213 209.016C169.111 209.375 172.01 209.5 175.002 209.5C177.994 209.5 180.893 209.375 183.791 209.008C244.549 204.219 290.236 152.133 290.236 152.133L294.877 147L290.236 141.875C290.236 141.875 240.127 84.5 175.002 84.5ZM175.002 100.125C192.213 100.125 208.08 104.828 221.877 111.109C227.01 119.574 229.713 129.288 229.689 139.188C229.723 152.655 224.782 165.66 215.815 175.708C206.849 185.756 194.487 192.139 181.104 193.633C180.947 193.664 180.768 193.602 180.619 193.633C178.752 193.719 176.893 193.875 175.002 193.875C172.924 193.875 170.916 193.75 168.9 193.633C155.516 192.139 143.155 185.756 134.189 175.708C125.222 165.66 120.281 152.655 120.314 139.188C120.314 128.992 123.064 119.5 127.885 111.352H127.643C141.541 104.945 157.604 100.125 175.002 100.125ZM106.643 123.078C105.372 128.355 104.717 133.76 104.689 139.188C104.689 152.891 108.596 165.711 115.432 176.539C101.563 168.414 88.8369 158.481 77.5879 147C86.4456 138.069 96.1795 130.06 106.643 123.078ZM243.361 123.078C253.824 130.057 263.558 138.072 272.416 147C261.167 158.481 248.441 168.414 234.572 176.539C241.619 165.357 245.344 152.404 245.314 139.188C245.314 133.602 244.58 128.273 243.361 123.078Z" fill="url(#iris-wordmark-eye)" />
    <path d="M166.658 127.414L140 135.69L166.486 143.621L174.914 170L182.826 143.621L210 135.69L182.654 127.414L174.914 100L166.658 127.414Z" fill="url(#iris-wordmark-spark)" />
    <defs>
      <radialGradient id="iris-wordmark-eye" cx="0" cy="0" r="1" gradientTransform="matrix(431.324 -98.6407 -150.368 -224.628 31.8468 222.597)" gradientUnits="userSpaceOnUse">
        <stop stopColor="#F7F7F5" />
        <stop offset="0.25" stopColor="#35C7DF" />
        <stop offset="0.409199" stopColor="#7B2FE2" />
        <stop offset="0.597863" stopColor="#C03FD0" />
        <stop offset="0.706731" stopColor="#D62086" />
        <stop offset="0.797333" stopColor="#9D1160" />
        <stop offset="0.870192" stopColor="#891255" />
        <stop offset="1" stopColor="#101014" />
      </radialGradient>
      <radialGradient id="iris-wordmark-spark" cx="0" cy="0" r="1" gradientTransform="matrix(125.934 -55.2388 -43.9031 -125.792 133.203 177.334)" gradientUnits="userSpaceOnUse">
        <stop stopColor="#F7F7F5" />
        <stop offset="0.25" stopColor="#35C7DF" />
        <stop offset="0.409199" stopColor="#7B2FE2" />
        <stop offset="0.597863" stopColor="#C03FD0" />
        <stop offset="0.706731" stopColor="#D62086" />
        <stop offset="0.797333" stopColor="#9D1160" />
        <stop offset="0.870192" stopColor="#891255" />
        <stop offset="1" stopColor="#101014" />
      </radialGradient>
    </defs>
  </svg>
);

// Exact lucide "life-buoy" icon used for "Help & Support" on the reference
// site's account menu.
export const LifeBuoyIcon: React.FC<{ fontSize?: "small" | "medium" }> = ({ fontSize = "small" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={fontSize === "small" ? 20 : 24}
    height={fontSize === "small" ? 20 : 24}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx={12} cy={12} r={10} />
    <path d="m4.93 4.93 4.24 4.24" />
    <path d="m14.83 9.17 4.24-4.24" />
    <path d="m14.83 14.83 4.24 4.24" />
    <path d="m9.17 14.83-4.24 4.24" />
    <circle cx={12} cy={12} r={4} />
  </svg>
);

// Exact lucide "arrow-up-right" icon used on the reference site's home-page
// prompt-suggestion chips.
export const ArrowUpRightIcon: React.FC<{ fontSize?: "small" | "medium" }> = ({ fontSize = "small" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={fontSize === "small" ? 14 : 18}
    height={fontSize === "small" ? 14 : 18}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M7 7h10v10" />
    <path d="M7 17 17 7" />
  </svg>
);
